import { isAfter } from 'date-fns'
import { type Seance, SeanceAdapter, type SeanceDTO } from '@/5_entities/Seance'
import type { Venue } from '@/5_entities/Venue'
import type { SearchResult } from '@/6_shared/model'
import type { Event, EventDTO } from './type'

export class EventAdapter implements Event {
  public id: Event['id']
  public title: Event['title']
  public seances: Event['seances']
  public promo: Event['promo']
  public gallery: Event['gallery']
  public galleryPreviews: Event['galleryPreviews']
  public image: Event['image']
  public imagePreview: Event['imagePreview']
  public imageMobile: Event['imageMobile']
  public description: Event['description']
  public timeInfo: Event['timeInfo']
  public ageCategory: Event['ageCategory']
  public eventSeancesCount: Event['eventSeancesCount']
  public seancesPagination: Event['seancesPagination']
  public organizer: Event['organizer']
  public categoryId: Event['categoryId']
  public minPrice: Event['minPrice']
  public venueNames: Event['venueNames']
  public aggregatedDate: Event['aggregatedDate']

  constructor(data: EventDTO) {
    this.id = data.id
    this.title = data.title
    this.seances = Array.isArray(data?.seances?.items) ? this._setSeances(data) : []
    this.eventSeancesCount = data?.seances?.pagination?.totalCount || 0
    this.promo = data.promo
    this.gallery = this._setGallery(data.media?.gallery)
    this.galleryPreviews = this._setGalleryPreviews(data.media?.previews, data.media?.gallery)
    this.image = data.media?.posterImage
    this.imagePreview = data.media?.previewPosterImage
    this.imageMobile = data.media?.mobilePosterImage
    this.description = data?.fullDescription
    this.timeInfo = data?.timeInfo
    this.ageCategory = data?.ageCategory
    this.seancesPagination = data?.seances?.pagination
    this.organizer = data?.organizer
    this.categoryId = data?.categoryId || 0
    this.minPrice = data?.aggregatedInfo?.price || undefined
    this.venueNames = data?.aggregatedInfo.venues || []
    this.aggregatedDate = data?.aggregatedInfo.date || ''
  }

  public get isPushkin() {
    return this.promo?.badges?.find((item) => item.type === 'isPushkin')?.value || false
  }

  public get beginsAt() {
    if (!this.seances.length) return undefined

    return this.seances[0].beginsAt
  }

  private _setGallery(gallery: EventDTO['media']['gallery']) {
    return Array.isArray(gallery)
      ? gallery.map((item, counter) => {
          return {
            ...item,
            object: {
              ...item.object,
              id: counter
            }
          }
        })
      : []
  }

  private _setGalleryPreviews(
    previews: EventDTO['media']['previews'],
    gallery: EventDTO['media']['gallery']
  ) {
    const previewsData = Array.isArray(previews)
      ? previews.map((item, counter) => {
          return {
            ...item,
            object: {
              ...item.object,
              id: item.object.id ?? counter
            }
          }
        })
      : []
    const galleryData = this._setGallery(gallery).splice(0, 3)

    return previewsData.length ? previewsData : galleryData
  }

  public get venues() {
    return this.seances.map((seance) => seance.venue).filter((item) => !!item) as Venue[]
  }

  private _setSeances(event: EventDTO) {
    const seances =
      event.seances?.items
        .map((item) => new SeanceAdapter(item.object))
        .filter((seance: Seance) => seance.forceDisplay || !!seance.ticketsLeft) ?? []

    seances.sort((a, b) => {
      if (isAfter(a.beginsAt, b.beginsAt)) return 1
      if (!isAfter(b.endsAt, b.endsAt)) return -1

      return 0
    })

    return seances
  }

  public addSeancesNextPage(newSeances: SearchResult<'seance', SeanceDTO>) {
    const { pagination, items } = newSeances
    this.seancesPagination = pagination
    const seances = items.map((item) => new SeanceAdapter(item.object))
    seances.forEach((seance) => this.seances.push(seance))
  }

  public findSeanceById = (id: Seance['id']) => this.seances.find((seance) => seance.id === id)
  public findSeanceIndexById = (id: Seance['id']) =>
    this.seances.findIndex((seance) => seance.id === id)
}
